import { Fragment, memo } from 'react';

import {
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
  SYSTEM_DATE_FORMAT_SHORT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmPreview } from 'APIPackage/KnmSearchModule/interfaces/IKnmPreview';
import { formatDate, formatDateLocale } from 'CorePackage/MainModule/common/formatDate';
import { getNewMappedStatus } from 'KnmErpPackage/MainModule/common/getNewMappedStatus';
import { KnmResultInfoTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmResultInfoTypeCodesEnum';

import styles from './KnmPreview.module.scss';
import { KnmSubtitle } from './KnmSubtitle';
import { convertArrayToDate } from '../common/convertArrayToDate';

interface IKnmCommonProps {
  passportData: IKnmPreview;
}

export const KnmCommon = memo(({ passportData }: IKnmCommonProps) => {
  const renderFzName = (fzName: string) => {
    const splitName = fzName.split(' ');

    if (splitName.length > 1) {
      return splitName.join('-');
    }

    return `${fzName}-ФЗ`;
  };

  return (
    <div className={styles.ColumnContent}>
      <KnmSubtitle title="Общий состав атрибутов паспорта КНМ">
        {passportData.erpId && (
          <div className={styles.Row}>
            <div className={styles.ColText}>
              Учетный номер КНМ в соответствии с постановлением Правительства РФ от 28 апреля 2015 г. N 415 &quot;О
              Правилах формирования и ведения единого реестра проверок&quot;
            </div>
            <div className={styles.ColValue}>{passportData.erpId}</div>
          </div>
        )}
        {passportData.erpCreateDate && (
          <div className={styles.Row}>
            <div className={styles.ColText}>
              Дата получения учетного номера КНМ в соответствии с постановлением Правительства РФ от 28 апреля 2015 г. N
              415 «О Правилах формирования и ведения единого реестра проверок»
            </div>
            <div className={styles.ColValue}>
              {formatDateLocale({
                date: convertArrayToDate(passportData.erpCreateDate),
                dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
                parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
              })}
            </div>
          </div>
        )}
        {passportData.typeName && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Вид КНМ</div>
            <div className={styles.ColValue}>{passportData.typeName}</div>
          </div>
        )}
        <div className={styles.Row}>
          <div className={styles.ColText}>Федеральный закон, в соответствии с которым проводится КНМ</div>
          <div className={styles.ColValue}>{passportData.fzName && renderFzName(passportData.fzName)}</div>
        </div>
        {passportData.status && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Статус КНМ</div>
            <div className={styles.ColValue}>
              {getNewMappedStatus(
                passportData.status,
                !!(
                  passportData.approve?.impossibleConductionReason ||
                  passportData.objects?.some(item =>
                    item.result?.resultInformation?.some(
                      resultInfo => resultInfo?.typeCode === KnmResultInfoTypeCodesEnum.UNIMPOSSIBLE_REASON_I,
                    ),
                  )
                ),
              )}
            </div>
          </div>
        )}
        {passportData.created && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата регистрации в ФГИС ЕРП</div>
            <div className={styles.ColValue}>
              {formatDateLocale({
                date: convertArrayToDate(passportData.created),
                dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
                parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
              })}
            </div>
          </div>
        )}
        {passportData.approve && passportData.approve.impossibleConductionReason && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Сведения о причинах невозможности проведения КНМ</div>
            <div className={styles.ColValue}>{passportData.approve.impossibleConductionReason}</div>
          </div>
        )}
      </KnmSubtitle>

      {passportData.classification && (
        <>
          <KnmSubtitle title="Общий состав атрибутов для классификации КНМ">
            {passportData.classification.carryoutTypeName && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Форма проведения КНМ</div>
                <div className={styles.ColValue}>{passportData.classification.carryoutTypeName}</div>
              </div>
            )}
            {passportData.classification.supervisionName && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Вид государственного контроля (надзора)</div>
                <div className={styles.ColValue}>{passportData.classification.supervisionName}</div>
              </div>
            )}
            {passportData.classification && passportData.classification.riskName && (
              <div className={styles.Row}>
                <div className={styles.ColText}>
                  Классификация КНМ по категориям риска и классу опасности (категории опасности) деятельности
                  проверяемого лица (юридического лица (ЮЛ) или индивидуального предпринимателя (ИП))
                </div>
                <div className={styles.ColValue}>{passportData.classification.riskName}</div>
              </div>
            )}
            {passportData.classification.noticeTypeName && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Способ уведомления проверяемого лица о проведении КНМ</div>
                <div className={styles.ColValue}>{passportData.classification.noticeTypeName}</div>
              </div>
            )}
            {passportData.classification.noticeDate && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Дата уведомления проверяемого лица о проведении КНМ</div>
                <div className={styles.ColValue}>
                  {formatDate({
                    date: convertArrayToDate(passportData.classification.noticeDate),
                    dateFormat: DATE_FORMAT_LONG_FNS,
                    parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
                  })}
                </div>
              </div>
            )}
            {passportData.classification.spendingTypeName && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Способ проведения контрольной закупки</div>
                <div className={styles.ColValue}>{passportData.classification.spendingTypeName}</div>
              </div>
            )}
            {passportData.classification.spendingDescription && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Описание закупки</div>
                <div className={styles.ColValue}>{passportData.classification.spendingDescription}</div>
              </div>
            )}
          </KnmSubtitle>
        </>
      )}

      {!!passportData.approve?.carryoutEvents?.length && (
        <>
          <KnmSubtitle title="Перечень мероприятий по контролю, необходимых для достижения целей и задач проведения проверки">
            {passportData.approve.carryoutEvents.map((event, eventIndex) => (
              <Fragment key={eventIndex}>
                <span className={styles.SectionTitle}>
                  {`Мероприятие по контролю, необходимое для достижения целей и задач проведения КНМ №${
                    eventIndex + 1
                  }`}
                </span>
                {event.eventsText && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>
                      Наименование мероприятия по контролю, необходимого для достижения целей и задач проведения КНМ
                    </div>
                    <div className={styles.ColValue}>{event.eventsText}</div>
                  </div>
                )}
                {event.startDate && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Дата начала проведения мероприятия</div>
                    <div className={styles.ColValue}>
                      {formatDate({
                        date: convertArrayToDate(event.startDate),
                        dateFormat: DATE_FORMAT_LONG_FNS,
                        parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
                      })}
                    </div>
                  </div>
                )}
                {event.endDate && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Дата окончания проведения мероприятия</div>
                    <div className={styles.ColValue}>
                      {formatDate({
                        date: convertArrayToDate(event.endDate),
                        dateFormat: DATE_FORMAT_LONG_FNS,
                        parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
                      })}
                    </div>
                  </div>
                )}
              </Fragment>
            ))}
          </KnmSubtitle>
        </>
      )}

      <KnmSubtitle title="Сведения о согласовании проведения КНМ с органом прокуратуры">
        {passportData.approve?.startDate && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата начала проведения КНМ</div>
            <div className={styles.ColValue}>
              {formatDate({
                date: convertArrayToDate(passportData.approve.startDate),
                dateFormat: DATE_FORMAT_LONG_FNS,
                parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
              })}
            </div>
          </div>
        )}
        {passportData.approve?.endDate && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата окончания проведения КНМ</div>
            <div className={styles.ColValue}>
              {formatDate({
                date: convertArrayToDate(passportData.approve.endDate),
                dateFormat: DATE_FORMAT_LONG_FNS,
                parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
              })}
            </div>
          </div>
        )}
        {passportData.approve?.startDateAsMonth && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Метка об указании только месяца проведения (КНМ) для плановых КНМ</div>
            <div className={styles.ColValue}>{passportData.approve.startDateAsMonth ? 'Да' : 'Нет'}</div>
          </div>
        )}
        {passportData.approve?.durationDay && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Срок проведения КНМ (в рабочих днях)</div>
            <div className={styles.ColValue}>{passportData.approve.durationDay}</div>
          </div>
        )}
        {passportData.approve?.durationHours && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Срок проведения КНМ (в рабочих часах)</div>
            <div className={styles.ColValue}>{passportData.approve.durationHours}</div>
          </div>
        )}
        {passportData.approve?.decisionPlace && (
          <div className={styles.Row}>
            <div className={styles.ColText}>
              Место вынесения решения о согласовании проведения КНМ органом прокуратуры
            </div>
            <div className={styles.ColValue}>{passportData.approve.decisionPlace}</div>
          </div>
        )}
        {passportData.approve?.decisionSignerTitle && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Должность подписанта о согласовании проведения КНМ органа прокуратуры</div>
            <div className={styles.ColValue}>{passportData.approve.decisionSignerTitle}</div>
          </div>
        )}
        {passportData.approve?.decisionSignerName && (
          <div className={styles.Row}>
            <div className={styles.ColText}>ФИО подписанта о согласовании проведения КНМ органа прокуратуры</div>
            <div className={styles.ColValue}>{passportData.approve.decisionSignerName}</div>
          </div>
        )}
        {passportData.approve?.reasons?.[0] && (
          <div className={styles.Row}>
            <div className={styles.ColText}>
              {(passportData.typeCode !== 'VP' &&
                'Отметка о включении плановой проверки (КНМ) в ежегодный сводный план проведения плановых проверок') ||
                'Сведения о согласовании проведения проверки с органами прокуратуры'}
            </div>
            <div className={styles.ColValue}>
              {passportData.approve.reasons[0]?.approveRequired === false && 'Не требует согласования'}
              {passportData.approve.reasons[0]?.approveRequired === true &&
                passportData.approve.approved === false &&
                'Не согласовано'}
              {passportData.approve.reasons[0]?.approveRequired === true &&
                passportData.approve.approved === true &&
                'Согласовано'}
            </div>
          </div>
        )}
        {passportData.approve?.inspectionTarget && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Цели, задачи, предмет КНМ</div>
            <div className={styles.ColValue}>{passportData.approve.inspectionTarget}</div>
          </div>
        )}
      </KnmSubtitle>

      {!!passportData.approve?.reasons?.length && (
        <>
          <KnmSubtitle title="Перечень правовых оснований">
            {passportData.approve.reasons.map((reason, reasonIndex) => (
              <Fragment key={reasonIndex}>
                <span className={styles.SectionTitle}>
                  {`Правовое основание проведения планового КНМ №${reasonIndex + 1}`}
                </span>
                {reason.text && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Основание проведения КНМ</div>
                    <div className={styles.ColValue}>{reason.text}</div>
                  </div>
                )}
                {reason.date && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>
                      Дата, характеризующая один из вариантов основания проведения КНМ как основание проведения
                    </div>
                    <div className={styles.ColValue}>
                      {formatDate({
                        date: convertArrayToDate(reason.date),
                        dateFormat: DATE_FORMAT_LONG_FNS,
                        parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
                      })}
                    </div>
                  </div>
                )}
              </Fragment>
            ))}
          </KnmSubtitle>
        </>
      )}

      {!!passportData.approve?.approveDocs?.length && (
        <>
          <KnmSubtitle title="Список атрибутов документов о согласовании (продлении) проверки">
            {passportData.approve.approveDocs.map((doc, docIndex) => (
              <Fragment key={docIndex}>
                <span className={styles.SectionTitle}>{`Документ о согласовании (продлении) КНМ №${
                  docIndex + 1
                }`}</span>
                {doc.name && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Документ о согласовании проведения КНМ</div>
                    <div className={styles.ColValue}>{doc.name}</div>
                  </div>
                )}
                {doc.number && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>
                      Номер распоряжения или приказа руководителя (заместителя руководителя) органа контроля (надзора) о
                      проведении КНМ
                    </div>
                    <div className={styles.ColValue}>{doc.number}</div>
                  </div>
                )}
                {doc.date && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>
                      Дата распоряжения или приказа руководителя (заместителя руководителя) органа контроля (надзора) о
                      продлении срока проведения КНМ
                    </div>
                    <div className={styles.ColValue}>
                      {formatDate({
                        date: convertArrayToDate(doc.date),
                        dateFormat: DATE_FORMAT_LONG_FNS,
                        parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
                      })}
                    </div>
                  </div>
                )}
              </Fragment>
            ))}
          </KnmSubtitle>
        </>
      )}

      {!!passportData.legalBases?.length && (
        <>
          <KnmSubtitle title="Выбранные правовые основания у проверки">
            {passportData.legalBases.map((lb, lbIndex) => (
              <Fragment key={lbIndex}>
                <span className={styles.SectionTitle}>{`Правовое основание КНМ №${lbIndex + 1}`}</span>
                {lb.atrDate && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Дата нормативно-правового акта</div>
                    <div className={styles.ColValue}>
                      {formatDate({
                        date: convertArrayToDate(lb.atrDate),
                        dateFormat: DATE_FORMAT_LONG_FNS,
                        parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
                      })}
                    </div>
                  </div>
                )}
                {lb.atrNumber && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Номер нормативно-правового акта</div>
                    <div className={styles.ColValue}>{lb.atrNumber}</div>
                  </div>
                )}
                {lb.documentName && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Положение нормативно-правового акта</div>
                    <div className={styles.ColValue}>{lb.documentName}</div>
                  </div>
                )}
                {lb.atrI && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Положение нормативно-правового акта.Глава</div>
                    <div className={styles.ColValue}>{lb.atrI}</div>
                  </div>
                )}
                {lb.atrII && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Положение нормативно-правового акта.Статья</div>
                    <div className={styles.ColValue}>{lb.atrII}</div>
                  </div>
                )}
                {lb.atrIII && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Положение нормативно-правового акта.Пункт</div>
                    <div className={styles.ColValue}>{lb.atrIII}</div>
                  </div>
                )}
                {lb.atrIV && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Положение нормативно-правового акта.Часть</div>
                    <div className={styles.ColValue}>{lb.atrIV}</div>
                  </div>
                )}
                {lb.typePointingName && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Тип положения нормативно-правового акта</div>
                    <div className={styles.ColValue}>{lb.typePointingName}</div>
                  </div>
                )}
              </Fragment>
            ))}
          </KnmSubtitle>
        </>
      )}
      {!!passportData?.requirements?.length && (
        <KnmSubtitle title="Обязательные требования">
          {passportData.requirements.map((requirement, rIndex) => (
            <Fragment key={rIndex}>
              <span className={styles.SectionTitle}>{`Обязательное требование №${rIndex + 1}`}</span>
              {typeof requirement?.name === 'string' && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Наименование обязательного требования</div>
                  <div className={styles.ColValue}>{requirement.name}</div>
                </div>
              )}
              {typeof requirement.knoName === 'string' && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Организация</div>
                  <div className={styles.ColValue}>{requirement.knoName}</div>
                </div>
              )}
              {typeof passportData?.classification?.supervisionName === 'string' && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Вид государственного контроля (надзора)</div>
                  <div className={styles.ColValue}>{passportData.classification.supervisionName}</div>
                </div>
              )}
              {!!requirement?.documents?.length && (
                <KnmSubtitle title="Требования">
                  {requirement.documents.map((doc, docIndex) => (
                    <Fragment key={docIndex}>
                      <span className={styles.SectionTitle}>{`Требование №${docIndex + 1}`}</span>
                      {typeof doc.props === 'string' && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>Формулировка требования</div>
                          <div className={styles.ColValue}>{doc.props}</div>
                        </div>
                      )}
                      {typeof doc.name === 'string' && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>Наименование нормативно-правового акта</div>
                          <div className={styles.ColValue}>{doc.name}</div>
                        </div>
                      )}
                      {typeof doc.number === 'string' && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>Номер нормативно-правового акта</div>
                          <div className={styles.ColValue}>{doc.number}</div>
                        </div>
                      )}
                      {!!doc.date?.length && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>Дата нормативно-правового акта</div>
                          <div className={styles.ColValue}>
                            {formatDate({
                              date: convertArrayToDate(doc.date),
                              dateFormat: DATE_FORMAT_LONG_FNS,
                              parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
                            })}
                          </div>
                        </div>
                      )}
                      {typeof doc.part === 'string' && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>Часть</div>
                          <div className={styles.ColValue}>{doc.part}</div>
                        </div>
                      )}
                      {typeof doc.paragraph === 'string' && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>Пункт</div>
                          <div className={styles.ColValue}>{doc.paragraph}</div>
                        </div>
                      )}
                      {typeof doc.article === 'string' && (
                        <div className={styles.Row}>
                          <div className={styles.ColText}>Статья</div>
                          <div className={styles.ColValue}>{doc.article}</div>
                        </div>
                      )}
                    </Fragment>
                  ))}
                </KnmSubtitle>
              )}
            </Fragment>
          ))}
        </KnmSubtitle>
      )}
    </div>
  );
});
